import { useRef, type KeyboardEvent, type ClipboardEvent } from 'react'
import { T } from '@/lib/design'

interface OtpInputProps {
  value: string
  onChange: (value: string) => void
  length?: number
}

export function OtpInput({ value, onChange, length = 6 }: OtpInputProps) {
  const inputs = useRef<(HTMLInputElement | null)[]>([])
  const digits = value.padEnd(length, '').split('').slice(0, length)

  const focus = (i: number) => inputs.current[i]?.focus()

  const handleChange = (i: number, raw: string) => {
    const char = raw.replace(/\D/g, '').slice(-1)
    const next = digits.map((d, idx) => (idx === i ? char : d)).join('').trimEnd()
    onChange(next)
    if (char && i < length - 1) focus(i + 1)
  }

  const handleKey = (i: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      e.preventDefault()
      if (digits[i]) {
        const next = digits.map((d, idx) => (idx === i ? '' : d)).join('').trimEnd()
        onChange(next)
      } else if (i > 0) {
        focus(i - 1)
      }
    } else if (e.key === 'ArrowLeft' && i > 0) {
      focus(i - 1)
    } else if (e.key === 'ArrowRight' && i < length - 1) {
      focus(i + 1)
    }
  }

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault()
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length)
    onChange(pasted)
    focus(Math.min(pasted.length, length - 1))
  }

  return (
    <div style={{
      width: '100%',
      display: 'grid',
      gridTemplateColumns: `repeat(${length}, minmax(0, 1fr))`,
      gap: 'clamp(4px, 1.8vw, 8px)',
    }}>
      {Array.from({ length }, (_, i) => (
        <input
          key={i}
          ref={(el) => { inputs.current[i] = el }}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={digits[i] ?? ''}
          onChange={(e) => handleChange(i, e.target.value)}
          onKeyDown={(e) => handleKey(i, e)}
          onPaste={handlePaste}
          onFocus={(e) => e.target.select()}
          style={{
            width: '100%',
            minWidth: 0,
            height: 'clamp(42px, 12vw, 52px)',
            textAlign: 'center',
            fontSize: 'clamp(17px, 5vw, 22px)',
            fontFamily: T.mono,
            fontWeight: 700,
            color: 'var(--text)',
            background: 'var(--surface)',
            border: `1px solid ${digits[i] ? T.gold : 'var(--border)'}`,
            borderRadius: 4,
            outline: 'none',
            transition: 'border-color 0.12s',
            caretColor: T.gold,
          }}
        />
      ))}
    </div>
  )
}
